import { useEffect, useState } from 'react';

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

// `beforeinstallprompt` fires once when the browser decides the PWA is installable.
// We must capture it at module scope because it can fire before any component mounts
// the hook; React's effect lifecycle is too late.
let deferredPrompt: BeforeInstallPromptEvent | null = null;
const subscribers = new Set<() => void>();

function notify(): void {
  subscribers.forEach((cb) => cb());
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    deferredPrompt = event as BeforeInstallPromptEvent;
    notify();
  });
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    notify();
  });
}

function detectIOS(): boolean {
  if (typeof navigator === 'undefined' || typeof document === 'undefined') return false;
  const ua = navigator.userAgent;
  // iPadOS 13+ masquerades as Mac; the touch check disambiguates.
  const isIPad = /Macintosh/.test(ua) && 'ontouchend' in document;
  return /iPhone|iPod|iPad/.test(ua) || isIPad;
}

function detectStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia('(display-mode: standalone)').matches) return true;
  type WithStandalone = Navigator & { standalone?: boolean };
  return (window.navigator as WithStandalone).standalone === true;
}

export type InstallOutcome = 'accepted' | 'dismissed' | 'unavailable';

export function useInstallPrompt(): {
  canInstall: boolean;
  isIOS: boolean;
  isStandalone: boolean;
  promptInstall: () => Promise<InstallOutcome>;
} {
  const [, setTick] = useState(0);

  useEffect(() => {
    const cb = (): void => setTick((n) => n + 1);
    subscribers.add(cb);
    return () => {
      subscribers.delete(cb);
    };
  }, []);

  const isStandalone = detectStandalone();
  const isIOS = detectIOS();
  const canInstall = !isStandalone && deferredPrompt !== null;

  async function promptInstall(): Promise<InstallOutcome> {
    if (!deferredPrompt) return 'unavailable';
    try {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      deferredPrompt = null;
      notify();
      return outcome;
    } catch (error) {
      console.error('Install prompt failed:', error);
      return 'unavailable';
    }
  }

  return { canInstall, isIOS, isStandalone, promptInstall };
}
